import type {CampaignDiscountModalState, CampaignProduct} from './types';
import type {PromotionFilter, PromotionFilterOption} from '../components/common/PromotionFilters/types';
import type {PageProps} from '@router';
import type {RadioChangeEvent} from '@ds/types';

import {CloseOutlined} from '@ant-design/icons';
import {BottomSheet, Button, Checkbox, Divider, Loading, Radio} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import CampaignProgress from './components/CampaignProgress/CampaignProgress';
import {useEffect, useRef, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  updateAllSelectedItems,
  updateDiscountOption,
  updateProduct,
  updateRegisterCampaignDefaultSelected,
  updateSelectedItems
} from 'store/slices/promotion-center/slicer';
import {
  approveProductList,
  getCycleDetails,
  getFilters,
  getProductsTotalList
} from 'store/slices/promotion-center/thunk';

import PromotionFilters from '../components/common/PromotionFilters/PromotionFilters';
import {INITIAL_PROMOTION_FILTERS} from '../components/common/PromotionFilters/constants';
import CampaignConfirmation from './components/CampaignConfirmation/CampaignConfirmation';
import CampaignProductDiscount from './components/CampaignProductDiscount/CampaignProductDiscount';
import CampaignProductItem from './components/CampaignProductItem/CampaignProductItem';
import CampaignSearchHeader from './components/CampaignSearchHeader/CampaignSearchHeader';
import {CAMPAIGN_PAGE_SIZE, CAMPAIGN_ROUTES} from './constants';
import {getCampaignVendorId} from './utils';

const Campaign: React.FC<PageProps> = ({headerData}) => {
  const vendorId = getCampaignVendorId();
  const {categoriesList, cycleDetails, approveLoading, selectedItems, productsTotalList, loading} =
    useAppSelector((state) => state.promotionCenter);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<{cycleId?: string}>();
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [discountModal, setDiscountModal] = useState<CampaignDiscountModalState>({
    isOpen: false,
    data: null
  });
  const [confirmModal, setConfirmModal] = useState({isOpen: false});
  const [selectAllModal, setSelectAllModal] = useState<{isOpen: boolean; data: {value: number}}>({
    isOpen: false,
    data: {value: 4}
  });
  const [pagination, setPagination] = useState({page: 1, pageSize: CAMPAIGN_PAGE_SIZE});
  const [pageState, setPageState] = useState<'campaign' | 'search'>('campaign');
  const [search, setSearch] = useState<string | null>(null);
  const [filters, setFilters] = useState<PromotionFilter[]>(() =>
    structuredClone(INITIAL_PROMOTION_FILTERS)
  );
  const [filtersValue, setFiltersValue] = useState<Record<string, unknown>>({});

  const handleRadioChange = ({name, e}: {name: string; e: RadioChangeEvent}) => {
    setFiltersValue({...filtersValue, [name]: e.target.value});
  };

  const activeFilterItem = (filter: PromotionFilter) => {
    setFilters((current) =>
      current.map((item) => {
        if (filter.name === item.name) {
          setFiltersValue({
            ...filtersValue,
            [filter.key ?? '']: !filter.selected ? item.name : null
          });
          return {...item, selected: !item.selected};
        }
        if (filter.key !== item.key) {
          return {...item, selected: item.selected};
        }
        return {...item, selected: false};
      })
    );
  };

  const handleFilter = ({
    selectedItem,
    name,
    checked
  }: {
    selectedItem: PromotionFilterOption;
    name: string;
    checked: boolean;
  }) => {
    setFilters((current) =>
      current.map((filter) => {
        if (filter.name !== name) {
          return filter;
        }
        return {
          ...filter,
          items: filter.items?.map((item) =>
            item.value === selectedItem.value ? {...item, selected: checked} : item
          )
        };
      })
    );
  };

  const getItems = ({pageSize, page}: {pageSize?: number; page?: number}) => {
    const preparedFilters: Record<string, unknown> = {};
    if (search?.length) {
      preparedFilters.search = search;
    } else {
      filters.forEach((filter) => {
        if (filter.type === 'checkbox' && filter.items?.length) {
          filter.items.forEach((item) => {
            if (item.selected) {
              preparedFilters[filter.name] = filter.name === 'category' ? item.id : item.value;
            }
          });
        }
      });
      const sortValue = filtersValue.sort as PromotionFilterOption | undefined;
      if (sortValue) {
        preparedFilters.sort_type = sortValue.value;
        preparedFilters.sort_by = sortValue.name;
      }
      const stockValue = filtersValue.stock as PromotionFilterOption | undefined;
      if (stockValue) {
        preparedFilters.stock = stockValue.value;
      }
      if (filtersValue.discount_share_type) {
        preparedFilters.discount_share_type = 'brand';
      }
      if (filtersValue.discount_type) {
        preparedFilters.discount_type = filtersValue.discount_type;
      }
    }
    void dispatch(
      getCycleDetails({
        vendor_id: vendorId,
        cycle_id: Number(params.cycleId),
        page_size: pageSize ? pageSize : pagination.pageSize,
        page: page ? page : pagination.page + 1,
        ...preparedFilters
      })
    );
  };

  const loadMore = () => {
    getItems({pageSize: pagination.pageSize, page: pagination.page + 1});
    setPagination({pageSize: pagination.pageSize, page: pagination.page + 1});
  };

  const removeFilter = (name: string) => {
    if (name === 'category') {
      setFilters((current) =>
        current.map((filter) =>
          name === filter.name
            ? {...filter, items: filter.items?.map((item) => ({...item, selected: false}))}
            : filter
        )
      );
      return;
    }
    if (name === 'sort') {
      setFiltersValue((current) => ({...current, sort: {}}));
      return;
    }
    if (name === 'stock') {
      setFiltersValue((current) => ({...current, stock: {}}));
    }
  };

  const handleProductSelection = (item: CampaignProduct) => {
    const products = (cycleDetails.products ?? []) as CampaignProduct[];
    const index = products.findIndex((product) => product.id === item.id);
    dispatch(updateSelectedItems(item.selected ? selectedItems + 1 : selectedItems - 1));
    dispatch(updateProduct({data: item, index} as never));
  };

  const setProductDiscount = () => {
    if (!discountModal.data) {
      return;
    }
    const products = (cycleDetails.products ?? []) as CampaignProduct[];
    const index = products.findIndex((product) => product.id === discountModal.data?.id);
    dispatch(updateProduct({data: discountModal.data, index} as never));
    setDiscountModal({isOpen: false, data: null});
  };

  const setDiscountOption = () => {
    if (selectAllModal.data.value !== 4) {
      dispatch(updateDiscountOption({value: selectAllModal.data.value} as never));
    }
    setSelectAllModal({isOpen: false, data: {value: 4}});
    setConfirmModal({isOpen: true});
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearch(value);
    setPagination({page: 1, pageSize: pagination.pageSize});
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    if (value.length >= 3) {
      searchTimeout.current = setTimeout(() => {
        void dispatch(
          getCycleDetails({
            vendor_id: vendorId,
            cycle_id: Number(params.cycleId),
            page_size: pagination.pageSize,
            page: 1,
            search: value
          })
        );
      }, 500);
      return;
    }
    if (value.length === 0) {
      void dispatch(
        getCycleDetails({
          vendor_id: vendorId,
          cycle_id: Number(params.cycleId),
          page_size: pagination.pageSize,
          page: 1
        })
      );
    }
  };

  const approveDeal = () => {
    const products = (productsTotalList.products ?? [])
      .filter((item: CampaignProduct) => item.selected === true)
      .map((item: CampaignProduct) => ({
        product_id: Number(item.product_id),
        vendor_discount: Number(item.vendor_default_discount)
      }));

    if (
      products.some(
        (item: {product_id: number; vendor_discount: number}) =>
          !Number.isInteger(item.product_id) || !Number.isInteger(item.vendor_discount)
      )
    ) {
      toast('مقدار تخفیف یکی از کالاها نامعتبر است.');
      return;
    }

    void dispatch(
      approveProductList({
        products,
        vendor_id: vendorId,
        cycle_id: Number(params.cycleId)
      })
    )
      .unwrap()
      .then(() => {
        localStorage.removeItem('totalProducts');
        toast(
          cycleDetails.state === 'in_dsc'
            ? 'با موفقیت به دوره تخفیف پیوستید. دوره در تاریخ شروع فعال می‌شود.'
            : 'تغییرات ذخیره شد'
        );
        navigate(CAMPAIGN_ROUTES.info);
      })
      .catch((error: {errors?: {message?: string}; message?: string}) =>
        toast(error.errors?.message || error.message || 'ثبت تخفیف با خطا مواجه شد.')
      );
  };

  useEffect(() => {
    headerData?.({
      action: () => setPageState('search')
    });
    void dispatch(
      getProductsTotalList({vendor_id: vendorId, cycle_id: Number(params.cycleId)})
    )
      .unwrap()
      .then(() => {
        void dispatch(
          getCycleDetails({
            vendor_id: vendorId,
            cycle_id: Number(params.cycleId),
            page_size: pagination.pageSize,
            page: 1
          })
        );
      });
    void dispatch(getFilters({vendor_id: vendorId, cycle_id: params.cycleId}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (categoriesList?.length) {
      setFilters((current) =>
        current.map((filter) =>
          filter.name === 'category' ? {...filter, items: categoriesList} : filter
        )
      );
    }
  }, [categoriesList]);

  useEffect(() => {
    setPagination({pageSize: pagination.pageSize, page: 1});
    getItems({pageSize: pagination.pageSize, page: 1});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersValue.discount_type, filtersValue.discount_share_type]);

  if (loading) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" />
      </div>
    );
  }

  const minCount = productsTotalList?.min_count_discount ?? 0;
  const canApprove = selectedItems >= minCount;

  return (
    <PageLayout
      id="container"
      header={
        pageState === 'campaign' ? (
          <PromotionFilters
            activeFilterItem={activeFilterItem}
            filters={filters}
            handleFilter={handleFilter}
            handleRadioChange={handleRadioChange}
            filtersValue={filtersValue}
            getItems={getItems}
            removeFilter={removeFilter}
            setPagination={setPagination}
            state={cycleDetails?.cycle?.state}
          />
        ) : (
          <CampaignSearchHeader
            value={search ?? ''}
            onChange={handleSearch}
            onClose={() => {
              setPageState('campaign');
              setSearch(null);
              void dispatch(
                getCycleDetails({
                  vendor_id: vendorId,
                  cycle_id: Number(params.cycleId),
                  page_size: pagination.pageSize,
                  page: pagination.page
                })
              );
            }}
          />
        )
      }
      footer={
        pageState === 'campaign' ? (
          canApprove ? (
            <Button
              fullWidth
              onClick={() => {
                setSelectAllModal({isOpen: true, data: {value: 4}});
              }}
            >{`تایید (${selectedItems} کالا) `}</Button>
          ) : (
            <div className="flex w-full flex-col gap-3.5">
              <div className="mb-2 flex justify-between text-body-sm">
                <span>{`حداقل کالا: ${minCount} عدد`}</span>
                <span>{`${minCount - selectedItems} کالای دیگر انتخاب کنید`}</span>
              </div>
              <CampaignProgress percentage={minCount ? (selectedItems * 100) / minCount : 0} />
            </div>
          )
        ) : undefined
      }
    >
      {pageState === 'search' && !cycleDetails.products?.length ? (
        <div className="flex min-h-60 flex-col items-center justify-center">
          <span className="text-body-sm text-neutral-fg">موردی یافت نشد!</span>
          <span className="text-body-sm text-neutral-fg">از کلمات آشناتر استفاده کنید.</span>
        </div>
      ) : null}
      <InfiniteScroll
        scrollableTarget="container"
        scrollThreshold={0.5}
        next={loadMore}
        pullDownToRefreshContent={<></>}
        pullDownToRefreshThreshold={50}
        hasMore={cycleDetails?.products?.length < cycleDetails?.total_items}
        loader={
          <div className="flex h-10 justify-center">
            <Loading size="medium" color="primary" />
          </div>
        }
        dataLength={cycleDetails?.products?.length || 0}
      >
        {pageState === 'campaign' ? (
          <div className="flex h-12.5 items-center gap-4 border-b border-gray-02 px-4 text-body-sm-emphasis">
            <Checkbox
              onChange={(event) => {
                dispatch(updateRegisterCampaignDefaultSelected());
                dispatch(updateAllSelectedItems(event.target.checked as never));
              }}
              checked={selectedItems === productsTotalList?.products?.length}
              disabled={approveLoading}
            />
            <span>انتخاب همه کالاها</span>
          </div>
        ) : null}
        <div className={approveLoading ? 'pointer-events-none opacity-50' : undefined}>
          {cycleDetails?.products?.map((item: CampaignProduct) => (
            <CampaignProductItem
              key={item.id}
              item={item}
              handleProductSelection={handleProductSelection}
              handleDiscountModal={(product) => setDiscountModal({isOpen: true, data: product})}
            />
          ))}
        </div>
      </InfiniteScroll>
      <BottomSheet
        show={discountModal.isOpen}
        onClose={() => setDiscountModal({isOpen: false, data: null})}
        header={
          <div className="flex items-center gap-4 p-3 text-subtitle-md text-neutral-fg">
            <CloseOutlined
              className="text-subtitle-lg"
              aria-hidden
              onClick={() => setDiscountModal({isOpen: false, data: null})}
            />
            <span>ویرایش تخفیف کالا</span>
          </div>
        }
      >
        {discountModal.data ? (
          <CampaignProductDiscount
            discountModal={discountModal}
            setDiscountModal={setDiscountModal}
            setProductDiscount={setProductDiscount}
          />
        ) : null}
      </BottomSheet>
      <BottomSheet
        show={confirmModal.isOpen}
        onClose={() => setConfirmModal({isOpen: false})}
        header={
          <div className="flex items-center gap-4 p-3 text-subtitle-md text-neutral-fg">
            <CloseOutlined
              className="text-subtitle-lg"
              aria-hidden
              onClick={() => setConfirmModal({isOpen: false})}
            />
            <span>
              {cycleDetails?.state === 'in_dsc' ? 'بازنگری و تایید' : null}
              {cycleDetails?.state === 'in_edsc' || cycleDetails?.state === 'in_hold'
                ? 'تغییرات ذخیره شود؟'
                : null}
            </span>
          </div>
        }
      >
        <div className="flex flex-col items-center gap-4 p-4">
          {cycleDetails?.state === 'in_dsc' ? <CampaignConfirmation /> : null}
          {cycleDetails?.state === 'in_edsc' || cycleDetails?.state === 'in_hold' ? (
            <p className="text-body-sm text-neutral-fg">
              با ذخیره کردن تغییرات، تنظیمات قبلی پاک شده و تنظیمات جدید جایگزین آن می‌شود.
            </p>
          ) : null}
          <Button onClick={approveDeal} fullWidth>
            {cycleDetails?.state === 'in_dsc' ? 'تایید و ثبت' : null}
            {cycleDetails?.state === 'in_edsc' ? 'تایید تغییرات' : null}
          </Button>
        </div>
      </BottomSheet>
      <BottomSheet
        show={selectAllModal.isOpen}
        onClose={() => setSelectAllModal({isOpen: false, data: {value: 4}})}
        header={
          <div className="flex items-center gap-4 p-3 text-subtitle-md text-neutral-fg">
            <CloseOutlined
              className="text-subtitle-lg"
              aria-hidden
              onClick={() => setSelectAllModal({isOpen: false, data: {value: 4}})}
            />
            <span>انتخاب مقدار تخفیف</span>
          </div>
        }
      >
        <div className="flex flex-1 flex-col p-5">
          <h3 className="text-subtitle-md text-neutral-fg">کدام یک از موارد تخفیف را میخواهید؟</h3>
          <Radio.Group
            onChange={(event) => {
              setSelectAllModal({...selectAllModal, data: {value: Number(event.target.value)}});
            }}
            value={String(selectAllModal.data.value)}
          >
            <div className="flex flex-1 flex-col gap-3.5">
              <Radio value="4">تخفیف‌های انتخاب شده</Radio>
              <Divider className="bg-gray-06" size={1} />
              <Radio value="3">بیشترین تخفیف</Radio>
              <Radio value="2">میانه تخفیف</Radio>
              <Radio value="1">کمترین تخفیف</Radio>
            </div>
          </Radio.Group>
        </div>
        <div className="flex flex-col items-center gap-4 p-4">
          <Button fullWidth onClick={setDiscountOption}>
            تایید
          </Button>
        </div>
      </BottomSheet>
    </PageLayout>
  );
};

export default Campaign;
